// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';

export interface RenameAppDialogProps {
  appId: string;
  /** Current display name; the prefill and the unchanged-guard baseline. */
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Renames the app's display name — catalog, marketplace, and header text only.
 * The slug (repo, namespace) and the serving URL are separate identities: the
 * slug never changes, the URL changes via ChangeUrlDialog (ADR-0050).
 */
export function RenameAppDialog({
  appId,
  name,
  open,
  onOpenChange,
}: RenameAppDialogProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const mutation = api.apps.update.useMutation();
  const [value, setValue] = React.useState(name);
  const [error, setError] = React.useState<string | null>(null);
  const draftSession = React.useRef({ appId, open: false });

  React.useEffect(() => {
    if (open && (!draftSession.current.open || draftSession.current.appId !== appId)) {
      setValue(name);
      setError(null);
    }
    draftSession.current = { appId, open };
  }, [open, name, appId]);

  const trimmed = value.trim();
  const unchanged = trimmed === name;

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const res = await mutation.mutateAsync({
        params: { id: appId },
        body: { name: trimmed },
      });
      if (res.status >= 300) throw res;
      toast.success(`Renamed to ${trimmed}.`);
      onOpenChange(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['apps.get', appId] }),
        queryClient.invalidateQueries({ queryKey: ['apps'] }),
      ]);
    } catch (err) {
      setError(mutationErrorMessage(err));
    }
  };

  return (
    <GLDrawer
      variant="confirm"
      mobileBreakpoint={768}
      open={open}
      onOpenChange={onOpenChange}
      preventDismiss={mutation.isPending}
      title={`Rename ${name}?`}
      description="Changes the display name shown in the catalog, marketplace, and this page. The app's URL and repository are not affected."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              unchanged || trimmed.length === 0 || trimmed.length > 255 || mutation.isPending
            }
          >
            {mutation.isPending ? 'Renaming…' : 'Rename'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Input
          value={value}
          aria-label="App name"
          aria-invalid={error !== null}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder={name}
        />
        {error && (
          <p className="text-sm text-gl-red-text" role="alert">
            {error}
          </p>
        )}
      </div>
    </GLDrawer>
  );
}
